import { stemmer } from "stemmer";
import { navItems } from "../components/Nav-Items";

/**
 * Global Search Utility
 * Extends the catalogSearch patterns for multi-type search across the application.
 * This is a separate utility that does NOT modify the existing catalogSearch.js
 */

// Search categories configuration
export const SEARCH_CATEGORIES = {
  PAGES: { key: "pages", label: "Pages", priority: 1 },
  PROJECTS: { key: "projects", label: "Projects", priority: 2 },
  CONTACTS: { key: "contacts", label: "Contacts", priority: 3 },
  TASKS: { key: "tasks", label: "Tasks", priority: 4 },
  RECEIPTS: { key: "receipts", label: "Receipts", priority: 5 },
};

/**
 * Tokenize a string into lowercase words
 * Keeps apostrophes (for possessives/contractions), removes other punctuation
 */
function tokenize(str, minLength = 2) {
  if (!str) return [];
  return str
    .toLowerCase()
    .replace(/[^a-z0-9'\s]/g, " ")
    .split(/\s+/)
    .filter((word) => word.length >= minLength);
}

/**
 * Preprocess email to remove the TLD (last dot and everything after)
 * This prevents matching on common TLDs like "com", "org", "net"
 * Example: "john@company.com" → "john@company"
 */
function preprocessEmail(email) {
  if (!email || typeof email !== "string" || !email.includes("@")) {
    return email;
  }
  return email.replace(/\.[^.]+$/, "");
}

/**
 * Build index for a single item with searchable fields
 */
function buildItemIndex(item, searchableFields) {
  const allTokens = [];
  const allStems = [];

  searchableFields.forEach((field) => {
    const value = item[field];
    if (value && typeof value === "string") {
      // If the field is 'email', preprocess it to remove TLD before tokenizing
      const valueToTokenize = field === "email" ? preprocessEmail(value) : value;
      const tokens = tokenize(valueToTokenize);
      allTokens.push(...tokens);
      allStems.push(...tokens.map((t) => stemmer(t)));
    }
  });

  return {
    item,
    tokens: allTokens,
    stemmed: allStems,
  };
}

/**
 * Flatten navItems to extract all pages (handles nested children)
 */
function flattenNavItems(items) {
  const pages = [];
  const flatten = (navList) => {
    navList.forEach((item) => {
      if (item.children && Array.isArray(item.children)) {
        // Add the parent item if it has a path
        if (item.path) {
          pages.push({
            id: item.path,
            name: item.name,
            path: item.path,
            type: "page",
          });
        }
        // Recursively process children
        flatten(item.children);
      } else if (item.path) {
        pages.push({
          id: item.path,
          name: item.name,
          path: item.path,
          type: "page",
        });
      }
    });
  };
  flatten(items);
  return pages;
}

/**
 * Build pages index from navItems (auto-discovery)
 * This is the single source of truth for searchable pages
 */
export function buildPagesIndex() {
  const pages = flattenNavItems(navItems);
  return pages.map((page) => buildItemIndex(page, ["name"]));
}

/**
 * Build index for projects
 */
export function buildProjectsIndex(projects) {
  if (!projects || projects.length === 0) return [];
  return projects.map((project) => {
    const item = {
      ...project,
      name: project.name,
      type: "project",
      // Create a searchable display for project code
      displayCode: project.user_project_number
        ? `PRJ-${project.user_project_number}`
        : project.project_code || "",
    };
    return buildItemIndex(item, ["name", "displayCode"]);
  });
}

/**
 * Build index for contacts
 */
export function buildContactsIndex(contacts) {
  if (!contacts || contacts.length === 0) return [];
  return contacts.map((contact) => {
    const item = {
      ...contact,
      name: contact.contact_name,
      type: "contact",
      // Keep original email in item (preprocessEmail is applied in buildItemIndex for tokenization)
      email: contact.email,
    };
    return buildItemIndex(item, ["contact_name", "company", "email"]);
  });
}

/**
 * Build index for tasks/todos
 */
export function buildTasksIndex(tasks) {
  if (!tasks || tasks.length === 0) return [];
  return tasks.map((task) => {
    const item = {
      ...task,
      name: task.task_name,
      type: "task",
    };
    return buildItemIndex(item, ["task_name", "description"]);
  });
}

/**
 * Build index for receipts
 */
export function buildReceiptsIndex(receipts) {
  if (!receipts || receipts.length === 0) return [];
  return receipts.map((receipt) => {
    const item = {
      ...receipt,
      name: receipt.original_name || receipt.filename || "Receipt",
      type: "receipt",
    };
    return buildItemIndex(item, ["original_name", "filename"]);
  });
}

/**
 * Build complete global search index
 */
export function buildGlobalSearchIndex(data) {
  return {
    pages: buildPagesIndex(),
    projects: buildProjectsIndex(data.projects),
    contacts: buildContactsIndex(data.contacts),
    tasks: buildTasksIndex(data.tasks),
    receipts: buildReceiptsIndex(data.receipts),
  };
}

/**
 * Normalize an email for comparison: lowercase and remove all spaces
 */
function normalizeEmail(email) {
  if (!email || typeof email !== "string") return "";
  return email.toLowerCase().replace(/\s+/g, "");
}

/**
 * Score an item for email-specific search
 * This is used when the query contains an '@' symbol
 * Returns score based on: exact match (1000), prefix match, or substring match
 */
function scoreEmailMatch(normalizedQuery, item) {
  // Get the original email from the item object
  const originalEmail = item.email;
  if (!originalEmail || typeof originalEmail !== "string") {
    return 0;
  }

  // Normalize the original email (lowercase, remove spaces)
  const normalizedEmail = normalizeEmail(originalEmail);

  // Check for exact match first
  if (normalizedQuery === normalizedEmail) {
    return 1000;
  }

  // Check for prefix match
  if (normalizedEmail.startsWith(normalizedQuery)) {
    return 500;
  }

  // Check for substring match using includes
  if (normalizedEmail.includes(normalizedQuery)) {
    return 100;
  }

  return 0;
}

/**
 * Score an indexed item against a search query
 * Same scoring logic as catalogSearch.js for consistency
 */
function scoreWithPrecomputed(queryWords, queryStemmed, itemWords, itemStemmed) {
  if (queryWords.length === 0 || itemWords.length === 0) return 0;

  // Exact normalized match gets highest priority
  const queryNormalized = queryWords.join(" ");
  const itemNormalized = itemWords.join(" ");
  if (queryNormalized === itemNormalized) {
    return 1000;
  }

  let score = 0;

  // Count how many query words (stemmed) appear in item
  let stemmedMatchCount = 0;
  for (const qs of queryStemmed) {
    if (itemStemmed.includes(qs)) {
      stemmedMatchCount++;
      score += 10;
    }
  }

  // Big bonus if ALL query words match
  if (stemmedMatchCount === queryStemmed.length && queryStemmed.length > 1) {
    score += 30;
  }

  // First word bonus
  const queryFirstStemmed = queryStemmed[0];
  const itemFirstStemmed = itemStemmed[0];

  if (queryFirstStemmed === itemFirstStemmed) {
    score += 25;

    // Extra bonus if exact match
    const queryFirstExact = queryWords[0];
    const itemFirstExact = itemWords[0];
    if (queryFirstExact === itemFirstExact) {
      score += 15;
    }
  }

  // Same word count suggests similar structure
  if (stemmedMatchCount > 0 && queryWords.length === itemWords.length) {
    score += 5;
  }

  // Prefix matching fallback
  if (stemmedMatchCount === 0) {
    for (const qw of queryWords) {
      if (qw.length < 2) continue;

      for (const iw of itemWords) {
        if (iw.length >= 3 && iw.startsWith(qw)) {
          score += 3;
          break;
        }
      }
    }
  }

  return score;
}

/**
 * Common TLDs for email domain detection
 * When a query contains a dot followed by one of these TLDs, treat it as an email lookup
 */
const COMMON_TLDS = [
  "com",
  "edu",
  "org",
  "net",
  "gov",
  "io",
  "co",
  "us",
  "uk",
  "ca",
  "au",
  "de",
  "fr",
  "jp",
  "cn",
  "in",
  "br",
  "ru",
  "info",
  "biz",
  "me",
];

/**
 * Check if query looks like an email domain (contains dot followed by a common TLD)
 * Examples: "gmail.com", "company.edu", "test.org"
 */
function looksLikeEmailDomain(query) {
  if (!query.includes(".")) return false;

  // Get text after the last dot and remove whitespace
  const afterLastDot = query.split(".").pop().replace(/\s+/g, "").toLowerCase();

  return COMMON_TLDS.includes(afterLastDot);
}

/**
 * Search a single category index
 */
function searchCategoryIndex(query, categoryIndex, limit = 5) {
  if (!query || !query.trim() || !categoryIndex || categoryIndex.length === 0) {
    return [];
  }

  const trimmedQuery = query.trim();

  // Check if query contains '@' or looks like an email domain - if so, use email-specific matching
  if (trimmedQuery.includes("@") || looksLikeEmailDomain(trimmedQuery)) {
    const normalizedQuery = normalizeEmail(trimmedQuery);
    const results = [];

    for (const indexed of categoryIndex) {
      const score = scoreEmailMatch(normalizedQuery, indexed.item);
      if (score > 0) {
        results.push({ item: indexed.item, score });
      }
    }

    results.sort((a, b) => b.score - a.score);
    return results.slice(0, limit).map((r) => r.item);
  }

  // Standard tokenization and stemming search
  const queryWords = tokenize(trimmedQuery);
  const queryStemmed = queryWords.map((w) => stemmer(w));

  if (queryWords.length === 0) return [];

  const results = [];
  for (const indexed of categoryIndex) {
    const score = scoreWithPrecomputed(
      queryWords,
      queryStemmed,
      indexed.tokens,
      indexed.stemmed
    );
    if (score > 0) {
      results.push({ item: indexed.item, score });
    }
  }

  results.sort((a, b) => b.score - a.score);
  return results.slice(0, limit).map((r) => r.item);
}

/**
 * Search across all categories and return grouped results
 */
export function searchGlobal(query, index, limits = {}) {
  const defaultLimits = {
    pages: 5,
    projects: 5,
    contacts: 5,
    tasks: 5,
    receipts: 5,
  };

  const effectiveLimits = { ...defaultLimits, ...limits };

  return {
    pages: searchCategoryIndex(query, index.pages, effectiveLimits.pages),
    projects: searchCategoryIndex(query, index.projects, effectiveLimits.projects),
    contacts: searchCategoryIndex(query, index.contacts, effectiveLimits.contacts),
    tasks: searchCategoryIndex(query, index.tasks, effectiveLimits.tasks),
    receipts: searchCategoryIndex(query, index.receipts, effectiveLimits.receipts),
  };
}

/**
 * Check if there are any results across all categories
 */
export function hasAnyGlobalResults(results) {
  if (!results) return false;
  return (
    results.pages.length > 0 ||
    results.projects.length > 0 ||
    results.contacts.length > 0 ||
    results.tasks.length > 0 ||
    results.receipts.length > 0
  );
}

/**
 * Get total result count across all categories
 */
export function getTotalResultCount(results) {
  if (!results) return 0;
  return (
    results.pages.length +
    results.projects.length +
    results.contacts.length +
    results.tasks.length +
    results.receipts.length
  );
}

/**
 * Highlight matching portions of text based on query
 * Returns an array of segments: [{ text: string, highlight: boolean }, ...]
 * 
 * Strategy:
 * 1. Try direct case-insensitive substring match first
 * 2. Fall back to token-based prefix matching for stemmed matches
 */
export function highlightMatches(text, query) {
  if (!text || !query || !query.trim()) {
    return [{ text: text || "", highlight: false }];
  }

  const trimmedQuery = query.trim();
  const textLower = text.toLowerCase();
  const queryLower = trimmedQuery.toLowerCase();

  // Strategy 1: Direct substring match
  const directIndex = textLower.indexOf(queryLower);
  if (directIndex !== -1) {
    const segments = [];
    if (directIndex > 0) {
      segments.push({ text: text.slice(0, directIndex), highlight: false });
    }
    segments.push({
      text: text.slice(directIndex, directIndex + trimmedQuery.length),
      highlight: true,
    });
    if (directIndex + trimmedQuery.length < text.length) {
      segments.push({
        text: text.slice(directIndex + trimmedQuery.length),
        highlight: false,
      });
    }
    return segments;
  }

  // Strategy 2: Token-based prefix matching
  const queryTokens = tokenize(trimmedQuery, 1); // Allow single char for highlighting
  if (queryTokens.length === 0) {
    return [{ text, highlight: false }];
  }

  // Find all word boundaries in the original text
  const wordPattern = /[a-zA-Z0-9']+/g;
  const segments = [];
  let lastIndex = 0;
  let match;

  while ((match = wordPattern.exec(text)) !== null) {
    const word = match[0];
    const wordStart = match.index;
    const wordLower = word.toLowerCase();

    // Add any non-word characters before this word
    if (wordStart > lastIndex) {
      segments.push({ text: text.slice(lastIndex, wordStart), highlight: false });
    }

    // Check if any query token is a prefix of this word
    let highlighted = false;
    for (const qt of queryTokens) {
      if (wordLower.startsWith(qt) && qt.length >= 2) {
        // Highlight only the matching prefix portion
        segments.push({ text: word.slice(0, qt.length), highlight: true });
        if (word.length > qt.length) {
          segments.push({ text: word.slice(qt.length), highlight: false });
        }
        highlighted = true;
        break;
      }
    }

    if (!highlighted) {
      segments.push({ text: word, highlight: false });
    }

    lastIndex = wordStart + word.length;
  }

  // Add any remaining text after the last word
  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), highlight: false });
  }

  // If no highlights were found, return the original text
  const hasHighlight = segments.some((s) => s.highlight);
  if (!hasHighlight) {
    return [{ text, highlight: false }];
  }

  return segments;
}
